import { useMemo } from 'react';
import { ReportFooter } from '../../../questionnaire/Report/ReportFooter/ReportFooter';
import { ReportHeader } from '../../../questionnaire/Report/ReportHeader/ReportHeader';
import { Divider } from '../../../ui/Divider/Divider';
import { RichTextArea } from '../../../ui/EditableInput/RichTextArea';
import { H2 } from '../../../ui/H2/H2';
import { Input } from '../../../ui/Input/Input';
import DatePicker from '../../../ui/DatePicker/DatePicker';
import CheckboxItem from '../../../ui/Checkbox/Checkbox';
import RadioButtonGroup from '../../../ui/RadioButton/RadioButtonGroup';
import { FundamentalRightsRisks } from './FundamentalRightsRisks';
import { CalendarIcon } from '../../../../icons/SvgIcons';
import {
  friaMetaInformation,
  systemDeployerInformation,
  useContext,
  roleOfSystemOptions,
  durationFrequencyOfUse,
  affectedPersonsGroups,
  vulnerableGroupsOptions,
  fundamentalRightsInScopeOptions,
  riskScenarioFields,
  likelihoodOptions,
  severityOptions,
  oversightModelOptions,
  humanOversight,
  mitigationCorrectiveMeasures,
  overallRiskMitigationStatusOptions,
  finalAssessmentDecision,
  overallDecisionOptions,
  RISK_SCENARIOS_KEY,
  VULNERABLE_GROUPS_KEY,
  ROLE_OF_SYSTEM_KEY,
  FUNDAMENTAL_RIGHTS_IN_SCOPE_KEY,
  OVERSIGHT_MODEL_KEY,
  OVERALL_RISK_MITIGATION_STATUS_KEY,
  OVERALL_DECISION_KEY
} from './FRIA.consts';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux';
import { setFRIAField } from '../../../../store/slices/documentationSlice';
import { RiskLevel } from '../../../ui/Classification/Classification.types';
import { ReportHeaderInfo } from '../../../questionnaire/Report/ReportHeader/ReportHeader.types';
import { ReportFooterProps } from '../../../questionnaire/Report/ReportFooter/ReportFooter.types';
import { FRIARiskScenario } from '../../../../types/FRIA.types';
import { EMAIL_REGEX } from '../../../../constants/Validation.constants';

export type FRIAHeaderData = ReportHeaderInfo;
export type FRIAFooterData = ReportFooterProps['componentInfo'];

interface FRIAProps {
  headerData?: FRIAHeaderData;
  footerData?: FRIAFooterData;
}

export const FRIA = ({ headerData, footerData }: FRIAProps) => {
  const dispatch = useAppDispatch();
  const { friaFields, createFRIAResponse } = useAppSelector(
    (state) => state.documentation
  );

  // Default header data if not provided
  const defaultHeaderData: FRIAHeaderData = useMemo(
    () => ({
      title: 'FRIA',
      entityType: { label: 'Entity Type', value: 'Deployer' },
      systemName: {
        label: 'AI System Name',
        value: createFRIAResponse?.documentControl?.aiSystemName || ''
      },
      version: {
        label: 'Version',
        value: createFRIAResponse?.documentControl?.version || '1.0'
      },
      organizationName: {
        label: 'Organization name',
        value: createFRIAResponse?.documentControl?.organizationName || ''
      },
      provider: {
        label: 'Compliance Contact person',
        value:
          createFRIAResponse?.documentControl?.complianceContactPerson || ''
      },
      classification: { risk: 'high' as RiskLevel, text: 'High Risk' },
      generatedTime:
        createFRIAResponse?.documentControl?.dateCreated ||
        new Date().toLocaleDateString(),
      reportId: createFRIAResponse?.documentControl?.documentId || '',
      labels: {
        generatedLabel: 'Date Created',
        idLabel: 'Document ID'
      }
    }),
    [createFRIAResponse]
  );

  const defaultFooterData: FRIAFooterData = useMemo(
    () => ({
      text:
        createFRIAResponse?.footerNote?.text ||
        'This Fundamental Rights Impact Assessment has been prepared in accordance with Article 27 of the EU AI Act.',
      email: 'compliai@skyhighgrowth.com',
      reportId: createFRIAResponse?.documentId || '',
      generatedBy:
        createFRIAResponse?.footerNote?.generatedBy ||
        'Generated by CompliAI by SHG'
    }),
    [createFRIAResponse]
  );

  const effectiveHeaderData = headerData || defaultHeaderData;
  const effectiveFooterData = footerData || defaultFooterData;

  // Handle field value changes
  const handleValueChange = (key: string, value: string, section: string) => {
    dispatch(setFRIAField({ label: key, value, section }));
  };

  // Handle checkbox group changes (for boolean flags stored as JSON)
  const handleCheckboxGroupChange = (
    storageKey: string,
    itemKey: string,
    checked: boolean,
    section: string
  ) => {
    const currentValue = friaFields[storageKey]?.value;
    let currentState: Record<string, boolean> = {};
    if (currentValue) {
      try {
        currentState = JSON.parse(currentValue);
      } catch (e) {
        console.error('Failed to parse checkbox state:', e);
      }
    }
    currentState[itemKey] = checked;
    dispatch(
      setFRIAField({
        label: storageKey,
        value: JSON.stringify(currentState),
        section
      })
    );
  };

  // Get checkbox state from stored JSON
  const getCheckboxState = (storageKey: string, itemKey: string): boolean => {
    const currentValue = friaFields[storageKey]?.value;
    if (currentValue) {
      try {
        const state = JSON.parse(currentValue);
        return state[itemKey] || false;
      } catch (e) {
        return false;
      }
    }
    return false;
  };

  // Handle radio button group changes
  const handleRadioGroupChange = (
    storageKey: string,
    selectedKey: string,
    section: string
  ) => {
    dispatch(
      setFRIAField({
        label: storageKey,
        value: selectedKey,
        section
      })
    );
  };

  // Risk scenarios initial value
  const riskScenariosInitialValue = useMemo((): Record<string, string>[] => {
    const getSelectedLevel = (
      level?: FRIARiskScenario['likelihood']
    ): string => {
      if (!level) return '';
      return (
        Object.entries(level).find(([_, value]) => value === true)?.[0] || ''
      );
    };

    const riskScenariosJson = friaFields[RISK_SCENARIOS_KEY]?.value;
    if (riskScenariosJson) {
      try {
        const scenarios = JSON.parse(riskScenariosJson) as FRIARiskScenario[];
        return scenarios.map((s) => ({
          'Risk ID': s.riskID || '',
          'Fundamental Right': s.fundamentalRight || '',
          'Risk Description': s.riskDescription || '',
          'Affected Persons/Groups': s.affectedPersonsGroups || '',
          'Existing controls': s.existingControls || '',
          Likelihood: getSelectedLevel(s.likelihood),
          Severity: getSelectedLevel(s.severity)
        }));
      } catch (error) {
        console.error('Failed to parse risk scenarios:', error);
      }
    } else if (createFRIAResponse?.fundamentalRightsRisks?.scenarios) {
      return createFRIAResponse.fundamentalRightsRisks.scenarios.map((s) => ({
        'Risk ID': s.riskID || '',
        'Fundamental Right': s.fundamentalRight || '',
        'Risk Description': s.riskDescription || '',
        'Affected Persons/Groups': s.affectedPersonsGroups || '',
        'Existing controls': s.existingControls || '',
        Likelihood: getSelectedLevel(s.likelihood),
        Severity: getSelectedLevel(s.severity)
      }));
    }
    return [];
  }, [friaFields, createFRIAResponse]);

  // Handle risk scenarios change
  const handleRiskScenariosChange = (rows: Record<string, string>[]) => {
    const scenarios: FRIARiskScenario[] = rows
      .filter(
        (row) =>
          row['Risk ID'] ||
          row['Fundamental Right'] ||
          row['Risk Description'] ||
          row['Existing controls']
      )
      .map((row) => ({
        riskID: row['Risk ID'] || '',
        fundamentalRight: row['Fundamental Right'] || '',
        riskDescription: row['Risk Description'] || '',
        affectedPersonsGroups: row['Affected Persons/Groups'] || '',
        existingControls: row['Existing controls'] || '',
        likelihood: {
          low: row.Likelihood === 'low',
          medium: row.Likelihood === 'medium',
          high: row.Likelihood === 'high'
        },
        severity: {
          low: row.Severity === 'low',
          medium: row.Severity === 'medium',
          high: row.Severity === 'high'
        }
      }));
    dispatch(
      setFRIAField({
        label: RISK_SCENARIOS_KEY,
        value: JSON.stringify(scenarios),
        section: 'Fundamental Rights Risks'
      })
    );
  };

  // Render a single field
  const renderField = (
    field: { label: string; key: string; placeholder: string; type?: string },
    section: string,
    twoInRow: boolean = false
  ) => {
    const fieldValue = friaFields[field.key]?.value || '';

    if (field.type === 'date') {
      const parsedDate = fieldValue ? new Date(fieldValue) : null;
      const validStartDate =
        parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate : null;

      return (
        <div
          key={field.key}
          className={`flex flex-col items-start gap-1 self-stretch ${
            twoInRow ? 'flex-1' : ''
          }`}
        >
          <label htmlFor={field.key} className="label">
            {field.label}
          </label>
          <DatePicker
            singleDate
            className="w-full"
            icon={CalendarIcon()}
            placeholder={field.placeholder}
            initialRange={{
              startDate: validStartDate,
              endDate: null
            }}
            onDateRangeChange={(range) => {
              const selected = range.startDate;
              handleValueChange(
                field.key,
                selected ? selected.toISOString().split('T')[0] : '',
                section
              );
            }}
          />
        </div>
      );
    }

    if (field.type === 'textArea') {
      return (
        <RichTextArea
          key={field.key}
          label={field.label}
          placeholder={field.placeholder}
          onChange={(value) => handleValueChange(field.key, value, section)}
          value={fieldValue}
          className={twoInRow ? 'w-full' : ''}
        />
      );
    }

    const emailError =
      field.type === 'email' &&
      fieldValue.length > 0 &&
      !EMAIL_REGEX.test(fieldValue)
        ? 'Please enter a valid email address'
        : null;

    return (
      <Input
        type={
          field.type === 'email'
            ? 'email'
            : field.type === 'date'
              ? 'date'
              : 'text'
        }
        key={field.key}
        id={field.key}
        label={field.label}
        maxLength={255}
        onChange={(e) => handleValueChange(field.key, e.target.value, section)}
        value={fieldValue}
        placeholder={field.placeholder}
        labelClassName="label"
        inputClassName="input"
        containerClassName={twoInRow ? 'flex-1' : ''}
        error={emailError}
      />
    );
  };

  // Render a section with fields
  const renderSection = (
    sectionData: {
      title: string;
      description?: string;
      fields: {
        label: string;
        key: string;
        placeholder: string;
        type?: string;
      }[];
    },
    twoInRow: boolean = false
  ) => (
    <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
      <H2 title={sectionData.title} textSize="text-base" />
      <Divider />
      {twoInRow ? (
        <div className="flex flex-col gap-3">
          {sectionData.fields
            .reduce<
              {
                label: string;
                key: string;
                placeholder: string;
                type?: string;
              }[][]
            >((rows, field, index) => {
              if (index % 2 === 0) {
                rows.push([field]);
              } else {
                rows[rows.length - 1].push(field);
              }
              return rows;
            }, [])
            .map((row, rowIndex) => (
              <div key={`row-${rowIndex}`} className="flex gap-3">
                {row.map((field) =>
                  renderField(field, sectionData.title, true)
                )}
              </div>
            ))}
        </div>
      ) : (
        sectionData.fields.map((field) => renderField(field, sectionData.title))
      )}
    </div>
  );

  // Render checkbox group
  const renderCheckboxGroup = (
    title: string,
    options: { label: string; key: string }[],
    storageKey: string,
    section: string
  ) => (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-gray-800 font-medium">{title}</p>
      <div className="flex flex-col gap-2">
        {options.map((option) => (
          <CheckboxItem
            key={option.key}
            label={option.label}
            value={option.key}
            checked={getCheckboxState(storageKey, option.key)}
            onChange={(_, checked) =>
              handleCheckboxGroupChange(
                storageKey,
                option.key,
                checked,
                section
              )
            }
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="w-full h-full p-5 flex flex-col gap-5">
      <ReportHeader componentInfo={effectiveHeaderData} />

      <Divider classes="my-3.5" />

      {/* FRIA Meta Information */}
      {renderSection(friaMetaInformation, true)}

      <Divider classes="my-3.5" />

      {/* System Deployer Information */}
      {renderSection(systemDeployerInformation)}

      <Divider classes="my-3.5" />

      {/* Use Context */}
      <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
        <H2 title={useContext.title} textSize="text-base" />
        <Divider />
        {useContext.fields
          .slice(0, 2)
          .map((field) => renderField(field, useContext.title))}

        {/* Role of System Checkboxes */}
        {renderCheckboxGroup(
          'Role of the system in decision-making',
          roleOfSystemOptions,
          ROLE_OF_SYSTEM_KEY,
          useContext.title
        )}

        {renderField(
          useContext.fields[useContext.fields.length - 1],
          useContext.title
        )}
      </div>

      <Divider classes="my-3.5" />

      {/* Duration & Frequency of Use */}
      {renderSection(durationFrequencyOfUse, true)}

      <Divider classes="my-3.5" />

      {/* Affected Persons & Groups */}
      <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
        <H2 title={affectedPersonsGroups.title} textSize="text-base" />
        <Divider />
        {affectedPersonsGroups.fields
          .slice(0, 2)
          .map((field) => renderField(field, affectedPersonsGroups.title))}

        {/* Vulnerable Groups Checkboxes */}
        {renderCheckboxGroup(
          'Vulnerable groups potentially affected',
          vulnerableGroupsOptions,
          VULNERABLE_GROUPS_KEY,
          affectedPersonsGroups.title
        )}

        {renderField(
          affectedPersonsGroups.fields[affectedPersonsGroups.fields.length - 1],
          affectedPersonsGroups.title
        )}
      </div>

      <Divider classes="my-3.5" />

      {/* Fundamental Rights Risks */}
      <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
        <H2 title="Fundamental Rights Risks" textSize="text-base" />
        <Divider />

        {/* Fundamental Rights In Scope */}
        {renderCheckboxGroup(
          'Fundamental rights in scope',
          fundamentalRightsInScopeOptions,
          FUNDAMENTAL_RIGHTS_IN_SCOPE_KEY,
          'Fundamental Rights Risks'
        )}

        <FundamentalRightsRisks
          title=""
          description="For each identified risk scenario, complete the form below. Add forms as needed."
          fields={riskScenarioFields}
          likelihoodOptions={likelihoodOptions}
          severityOptions={severityOptions}
          onChange={handleRiskScenariosChange}
          initialValue={riskScenariosInitialValue}
        />
      </div>

      <Divider classes="my-3.5" />

      {/* Human Oversight */}
      <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
        <H2 title={humanOversight.title} textSize="text-base" />
        <Divider />

        {/* Oversight Model Checkboxes */}
        {renderCheckboxGroup(
          'Oversight model',
          oversightModelOptions,
          OVERSIGHT_MODEL_KEY,
          humanOversight.title
        )}

        {humanOversight.fields.map((field) =>
          renderField(field, humanOversight.title)
        )}
      </div>

      <Divider classes="my-3.5" />

      {/* Mitigation & Corrective Measures */}
      {renderSection(mitigationCorrectiveMeasures)}

      <Divider classes="my-3.5" />

      {/* Final Assessment Decision */}
      <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
        <H2 title={finalAssessmentDecision.title} textSize="text-base" />
        <Divider />

        {/* Overall Risk Mitigation Status */}
        <RadioButtonGroup
          title="Overall risk mitigation status"
          name="overallRiskMitigationStatus"
          options={overallRiskMitigationStatusOptions.map((opt) => ({
            label: opt.label,
            value: opt.key
          }))}
          selectedValue={
            friaFields[OVERALL_RISK_MITIGATION_STATUS_KEY]?.value || ''
          }
          onChange={(value) =>
            handleRadioGroupChange(
              OVERALL_RISK_MITIGATION_STATUS_KEY,
              value,
              finalAssessmentDecision.title
            )
          }
        />

        {finalAssessmentDecision.fields.map((field) =>
          renderField(field, finalAssessmentDecision.title)
        )}
      </div>

      <Divider classes="my-3.5" />

      {/* Overall Decision */}
      <div className="flex flex-col gap-5 border border-gray-100 rounded-lg p-4">
        <H2 title="Overall Decision" textSize="text-base" />
        <Divider />

        <RadioButtonGroup
          title=""
          name="overallDecision"
          options={overallDecisionOptions.map((opt) => ({
            label: opt.label,
            value: opt.key
          }))}
          selectedValue={friaFields[OVERALL_DECISION_KEY]?.value || ''}
          onChange={(value) =>
            handleRadioGroupChange(
              OVERALL_DECISION_KEY,
              value,
              'Overall Decision'
            )
          }
        />
      </div>

      <Divider classes="my-3.5" />

      <ReportFooter componentInfo={effectiveFooterData} />
    </div>
  );
};
